import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {detectNewlyAddedAgents} from '@github-ui/agent-sessions/utils/agent-helpers'
import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import type {SelectPanelItemProps as ItemProps} from '@primer/react'
import {Label} from '@primer/react'
import {useCallback, useEffect, useRef} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {IAssignee} from '../../api/common-contracts'
import type {SuggestedAssignee} from '../../api/memex-items/contracts'
import {useCopilotAssignmentModal} from '../../components/table/copilot-assignment-modal-provider'
import {isCopilot} from '../../helpers/assignee-helper'
import {hasModelChanged} from '../../helpers/has-model-changed'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useFetchSuggestedAssignees} from '../../state-providers/suggestions/use-fetch-suggested-assignees'
import {useUpdateItem} from '../use-update-item'
import styles from './use-assignees-editor.module.css'

export const getLogin = (option: SuggestedAssignee) => {
  return option.login
}

export const convertOptionToItem = (
  option: SuggestedAssignee,
  selectedAssignees: Array<SuggestedAssignee>,
): ItemProps & SuggestedAssignee => {
  const selected = selectedAssignees.find(assignee => assignee.id === option.id)

  // For agents, show the owner (e.g., "GitHub", "Anthropic", "OpenAI") as description
  // Otherwise show the user's name
  const displayDescription = option.isAgent && option.agentDisplayOwner ? option.agentDisplayOwner : option.name

  return {
    ...option,
    leadingVisual() {
      return isCopilot(option.login) ? (
        <CopilotAvatar className={styles.GitHubAvatar} isDecorative />
      ) : (
        <GitHubAvatar src={option.avatarUrl} className={styles.GitHubAvatar} />
      )
    },
    trailingVisual: option.isAgent
      ? () => {
          return <Label>AI</Label>
        }
      : undefined,
    text: option.login,
    description: displayDescription ?? undefined,
    descriptionVariant: 'inline',
    groupId: selected ? 'assigned' : 'suggestions',
    ...testIdProps('table-cell-editor-row'),
  }
}

type UseAssigneesEditorProps = {
  model: MemexItemModel
}

type UseAssigneesEditorReturn = {
  fetchOptions: () => void
  saveSelected: (nextSelected: Array<SuggestedAssignee>, onSaved?: () => void) => Promise<void>
}

const assigneesAccessor = (model: MemexItemModel | Partial<MemexItemModel>) => model.columns?.Assignees

export function useAssigneesEditor({model}: UseAssigneesEditorProps): UseAssigneesEditorReturn {
  const {updateItem} = useUpdateItem()
  const {fetchSuggestedAssignees, clearSuggestedAssigneesCache} = useFetchSuggestedAssignees()
  const {openModal} = useCopilotAssignmentModal()

  const prevModelRef = useRef<MemexItemModel | undefined>(undefined)

  useEffect(() => {
    // for background syncs that may update the model out from under us
    const hasAssigneesChanged = hasModelChanged(prevModelRef.current, model, assigneesAccessor)
    if (hasAssigneesChanged) {
      clearSuggestedAssigneesCache(model)
    }
    prevModelRef.current = model
  }, [model, clearSuggestedAssigneesCache])

  const fetchOptions = useCallback(() => {
    return fetchSuggestedAssignees(model)
  }, [model, fetchSuggestedAssignees])

  const saveSelected = useCallback(
    async (nextSelected: Array<SuggestedAssignee>, onSaved?: () => void) => {
      // Detect any newly added agents
      const {newlyAddedAgents: agentsToAssign, nonAgentAssignees: assigneesToSave} = detectNewlyAddedAgents<
        IAssignee,
        SuggestedAssignee
      >(
        model.getAssignees(),
        nextSelected,
        a => a.isAgent || false,
        a => a.login,
      )

      const nameWithOwner = model.getNameWithOwner()
      const issueNumber = model.getItemNumber()

      const shouldOpenModal = Boolean(
        agentsToAssign.length > 0 && nameWithOwner && nameWithOwner.length === 2 && issueNumber,
      )

      if (shouldOpenModal) {
        // apply non-agent assignees immediately if any
        if (assigneesToSave.length > 0) {
          await updateItem(model, {
            dataType: MemexColumnDataType.Assignees,
            value: assigneesToSave,
          })
          clearSuggestedAssigneesCache(model)
        }

        // Then open modal for agent assignment
        const [owner, repo] = nameWithOwner as [string, string]

        const repository: CopilotAssignmentRepository = {
          name: repo,
          owner: {
            login: owner,
          },
        }

        const onSuccess = async () => {
          // Skip update in callback as agent_assignment_controller already handles it
          clearSuggestedAssigneesCache(model)
          onSaved?.()
        }

        const toSelectedAgent = (agent: SuggestedAssignee): SelectedAgent => ({
          typeName: 'topLevel' as const,
          displayName: agent.name ?? agent.login,
          avatarUrl: agent.avatarUrl,
          login: agent.login,
          botGlobalRelayId: agent.botGlobalRelayId ?? agent.global_relay_id,
        })

        openModal(repository, [issueNumber], onSuccess, agentsToAssign.map(toSelectedAgent))
        // Early return - don't execute the normal assignment flow below
        return
      }

      // Normal assignment flow (no Copilot involved)
      const hasChanged = hasModelChanged(
        model,
        {columns: {Assignees: nextSelected}},
        cbModel => cbModel.columns?.Assignees,
      )
      if (hasChanged) {
        await updateItem(model, {
          dataType: MemexColumnDataType.Assignees,
          value: nextSelected,
        })
        clearSuggestedAssigneesCache(model)
      }

      if (onSaved) {
        onSaved()
      }
    },
    [model, updateItem, clearSuggestedAssigneesCache, openModal],
  )

  return {
    fetchOptions,
    saveSelected,
  }
}
